/**
 * Which icon, colour and label a notification gets from its `notification_type`.
 *
 * WHY THIS IS ITS OWN MODULE. The rule used to live inline in
 * NotificationCenter as a six-entry table matched by substring:
 *
 *     const TYPE_CONFIG = { mention, approval, deadline, alert, felix, system }
 *     for (const [k, v] of Object.entries(TYPE_CONFIG))
 *       if (type.toLowerCase().includes(k)) return v;
 *     return TYPE_CONFIG.system;
 *
 * Measured against production on 2026-08-18: **all twelve distinct
 * notification types matched nothing and fell through to `system`.** Every one
 * of 7,784 rows rendered as the same grey gear labelled "System" —
 *
 *     WARNING 6450 · INFO 973 · ERROR 219 · QA_AGENT_FAILURE 36 ·
 *     iteration_review_ready 29 · MEETING_COMPLETED 22 ·
 *     ACTION_ITEM_ASSIGNED 16 · MEETING_REMINDER_15MIN 13 ·
 *     MEETING_REMINDER_5MIN 9 · QA_AGENT_SUCCESS 8 · EMAIL 7 ·
 *     TASK_ASSIGNED 2
 *
 * — so 219 errors were visually indistinguishable from 973 routine notices.
 * Nothing threw: the lookup ran, returned a config and rendered an icon. It
 * simply always returned the same one, which is why it survived. The only key
 * that could ever have matched real data was `alert`, via MEETING_RISK_ALERT,
 * and that type has zero rows.
 *
 * The vocabulary below is the vocabulary the backend ACTUALLY emits, read out
 * of the notifications table rather than imagined. `notification_type` is
 * `string | null` with no enum and no DB constraint, so this must degrade
 * gracefully for anything new — but degrading gracefully is what hid the
 * defect, so the module also exports the classifier for tests to hold to
 * account against real data.
 */
import {
  AlertTriangle, Bell, Bot, Calendar, Check, CheckCircle2, Clock,
  Info, Mail, MessageSquare, Settings, XCircle,
} from "lucide-react";

export interface NotificationTypeConfig {
  icon: React.ElementType;
  /** Tailwind text colour. Severity is never carried by colour alone — the
   *  icon and the label differ too, so this survives greyscale and a screen
   *  reader reading the label. */
  color: string;
  label: string;
}

const SYSTEM: NotificationTypeConfig = {
  icon: Settings, color: "text-slate-500", label: "System",
};

/**
 * EXACT matches first, keyed on the literal values production emits.
 * Case-insensitive: the table mixes `WARNING` with `iteration_review_ready`.
 */
const EXACT: Record<string, NotificationTypeConfig> = {
  error: { icon: XCircle, color: "text-red-500", label: "Error" },
  critical: { icon: XCircle, color: "text-red-500", label: "Critical" },
  warning: { icon: AlertTriangle, color: "text-amber-500", label: "Warning" },
  info: { icon: Info, color: "text-blue-500", label: "Info" },
  email: { icon: Mail, color: "text-blue-500", label: "Email" },
  qa_agent_failure: { icon: XCircle, color: "text-red-500", label: "QA failed" },
  qa_agent_success: { icon: CheckCircle2, color: "text-emerald-500", label: "QA passed" },
  iteration_review_ready: { icon: Bot, color: "text-brand-500", label: "Review ready" },
  meeting_completed: { icon: CheckCircle2, color: "text-emerald-500", label: "Meeting ended" },
  meeting_reminder_15min: { icon: Calendar, color: "text-amber-500", label: "Meeting soon" },
  meeting_reminder_5min: { icon: Calendar, color: "text-amber-500", label: "Meeting soon" },
  action_item_assigned: { icon: Check, color: "text-emerald-500", label: "Action item" },
  task_assigned: { icon: Check, color: "text-emerald-500", label: "Assigned" },
  // Customer-app liveness (WP-2.1). Registered here rather than left to the
  // substring fallback: neither name contains any needle in CONTAINS, so both
  // would have rendered as the grey "System" gear — an app being DOWN looking
  // identical to a routine notice is the exact failure this module was written
  // to fix.
  customer_app_down: { icon: XCircle, color: "text-red-500", label: "App down" },
  customer_app_recovered: {
    icon: CheckCircle2, color: "text-emerald-500", label: "App recovered",
  },
};

/**
 * SUBSTRING fallback, tried in order, for types this file has not seen.
 *
 * Order is load-bearing and deliberate: a type containing two of these words
 * takes the FIRST, so severity beats category — `MEETING_RISK_ALERT` should
 * read as an alert, not as a meeting. Kept because the backend adds types
 * without telling the frontend, and a new `..._FAILED` should look wrong
 * immediately rather than after someone notices it looks like everything else.
 */
const CONTAINS: Array<[string, NotificationTypeConfig]> = [
  ["critical", EXACT.critical],
  ["fail", { icon: XCircle, color: "text-red-500", label: "Failed" }],
  ["error", EXACT.error],
  ["alert", { icon: AlertTriangle, color: "text-red-500", label: "Alert" }],
  ["risk", { icon: AlertTriangle, color: "text-amber-500", label: "Risk" }],
  ["warn", EXACT.warning],
  ["overdue", { icon: Clock, color: "text-amber-500", label: "Overdue" }],
  ["deadline", { icon: Clock, color: "text-amber-500", label: "Deadline" }],
  ["reminder", { icon: Calendar, color: "text-amber-500", label: "Reminder" }],
  ["success", { icon: CheckCircle2, color: "text-emerald-500", label: "Success" }],
  ["approval", { icon: Check, color: "text-emerald-500", label: "Approval" }],
  ["assigned", { icon: Check, color: "text-emerald-500", label: "Assigned" }],
  ["mention", { icon: MessageSquare, color: "text-blue-500", label: "Mention" }],
  ["comment", { icon: MessageSquare, color: "text-blue-500", label: "Comment" }],
  ["felix", { icon: Bot, color: "text-brand-500", label: "Felix" }],
  ["meeting", { icon: Calendar, color: "text-blue-500", label: "Meeting" }],
  ["email", EXACT.email],
  ["info", EXACT.info],
];

export function notificationTypeConfig(
  type: string | null | undefined,
): NotificationTypeConfig {
  if (!type) return SYSTEM;
  const key = String(type).trim().toLowerCase();
  if (!key) return SYSTEM;

  const exact = EXACT[key];
  if (exact) return exact;

  for (const [needle, config] of CONTAINS) {
    if (key.includes(needle)) return config;
  }
  return SYSTEM;
}

/** Exported for the tests, which assert against the real production vocabulary. */
export const SYSTEM_FALLBACK = SYSTEM;
